import { isMotivisionTrainer } from '../utils';
import React, { createContext, useContext, useEffect, useRef } from 'react';
import _ from 'lodash';
import {
  updateExerciseData,
  createGlobalExercise,
  createPersonalizedExercise,
  uploadExerciseImages,
  uploadExerciseVideos,
  deleteExercise,
  createNewGroup,
  updateGroupData,
  deleteGroupById,
  updateGymData,
  createNewManager,
  updateManagerData,
  createNewMember,
  updateMemberData,
  updateMemberAndSubscriptions,
  createNewProgram,
  updateProgramData,
  deleteProgramById,
  assignProgramToMembers,
  fetchSessionStatistics,
  fetchLastSessionStatistics,
  fetchLastSessions,
  createNewTemplate,
  updateTemplateData,
  deleteTemplateById,
  createNewTrainer,
  updateTrainerData,
  getJetsonsHealthByGymId,
  fetchMembersByIds,
  fetchGroupsByIds,
  fetchTrainersByIds,
  fetchManagersByIds,
  fetchTrainerPrograms,
  fetchTrainerTemplates,
  fetchGym,
} from '../api';
import { useErrorContext } from './errorProvider';
import { useGlobalAction } from './globalActionProvider';
import { useSessionData } from './sessionDataProvider';
import { useSessionAction } from './sessionActionProvider';

import type {
  Exercise,
  FileResponse,
  Group,
  Gym,
  Member,
  Manager,
  Program,
  AssignedProgram,
  SessionStatistic,
  TrainingRoutineTemplate,
  Trainer,
  WithOptionalId,
  GymProduct,
  JetsonHealthDTO,
  TrainingRoutineSession,
} from '../types';
import { Product } from '../constants';
import { logCache } from '../logger';

const SESSION_STATS_CACHE_TTL_MS = 60 * 1000;
const CACHE_NAMES = {
  SESSION_STATS: 'sessionStats',
  LAST_STATS: 'lastSessionStats',
  LAST_SESSIONS: 'lastSessions',
} as const;

type CacheEntry<T> = {
  data: T;
  expiresAt: number;
};

type CacheStore<T> = Record<string, CacheEntry<T>>;

const buildCacheKey = (
  memberIds: string[],
  extras: Record<string, unknown> = {},
): string => {
  const normalizedMemberIds = [...memberIds].sort();
  return JSON.stringify({
    members: normalizedMemberIds,
    ...extras,
  });
};

function getCachedValue<T>(
  cacheRef: React.MutableRefObject<CacheStore<T>>,
  cacheKey: string,
  cacheName: string,
): T | null {
  const entry = cacheRef.current[cacheKey];
  if (!entry) {
    logCache(`${cacheName}:MISS`, { cacheKey });
    return null;
  }
  if (entry.expiresAt < Date.now()) {
    delete cacheRef.current[cacheKey];
    logCache(`${cacheName}:EXPIRED`, { cacheKey });
    return null;
  }
  logCache(`${cacheName}:HIT`, { cacheKey });
  return entry.data;
}

function setCachedValue<T>(
  cacheRef: React.MutableRefObject<CacheStore<T>>,
  cacheKey: string,
  data: T,
  cacheName: string,
): void {
  cacheRef.current[cacheKey] = {
    data,
    expiresAt: Date.now() + SESSION_STATS_CACHE_TTL_MS,
  };
  logCache(`${cacheName}:SET`, {
    cacheKey,
    ttlMs: SESSION_STATS_CACHE_TTL_MS,
  });
}

export interface UserActionContextProps {
  updateExercise: (
    oldExerciseData: Exercise,
    newExerciseData: Exercise,
  ) => Promise<boolean>;
  createExercise: (
    newExercise: WithOptionalId<Exercise>,
    options: {
      baseExercise: Exercise | null;
      isCreatingPersonalizedExercise?: boolean;
    },
  ) => Promise<boolean>;
  deleteUserExercise: (exerciseId: string) => Promise<boolean>;
  uploadUserExerciseImages: (
    files: File[],
    trainerId: string,
  ) => Promise<FileResponse[]>;
  uploadUserExerciseVideos: (
    files: File[],
    trainerId: string,
  ) => Promise<FileResponse[]>;
  createGroup: (
    newGroup: WithOptionalId<Group>,
    trainerIds: string[],
    memberIds: string[],
  ) => Promise<boolean>;
  updateGroup: (oldGroupData: Group, newGroupData: Group) => Promise<boolean>;
  deleteGroup: (group: Group) => Promise<boolean>;
  updateGym: (gym: Gym) => Promise<boolean>;
  createManager: (manager: WithOptionalId<Manager>) => Promise<boolean>;
  updateManager: (manager: Manager) => Promise<boolean>;
  createMember: (
    member: WithOptionalId<Member>,
    trainerId?: string,
  ) => Promise<boolean | string>;
  updateMember: (
    oldMemberData: Member,
    newMemberData: Member,
  ) => Promise<boolean | string>;
  updateMemberSubscriptions: (
    oldMemberData: Member,
    newMemberData: Member,
    newSelectedProduct: Product | '',
    gymProducts: GymProduct[],
  ) => Promise<boolean>;
  createProgram: (
    program: WithOptionalId<Program>,
    trainerId: string,
  ) => Promise<{ success: boolean; program: Program | null }>;
  updateProgramAndRefreshPrograms: (program: Program) => Promise<boolean>;
  deleteProgram: (program: Program) => Promise<boolean>;
  assignProgramAndRefreshMembers: (
    memberIds: string[],
    program: AssignedProgram,
  ) => Promise<boolean>;
  getSessionStatistics: (
    memberIds: string[],
    from: string,
    to: string,
    useDefaultFromDate?: boolean,
  ) => Promise<Record<string, SessionStatistic[]>>;
  getLastSessionStatistics: (
    memberIds: string[],
  ) => Promise<Record<string, SessionStatistic>>;
  getLastSessions: (
    memberIds: string[],
  ) => Promise<Record<string, TrainingRoutineSession> | null>;
  createRoutineTemplate: (
    template: WithOptionalId<TrainingRoutineTemplate>,
    trainerId: string,
  ) => Promise<boolean>;
  updateTemplate: (template: TrainingRoutineTemplate) => Promise<boolean>;
  deleteTemplate: (template: TrainingRoutineTemplate) => Promise<boolean>;
  createTrainer: (
    trainer: WithOptionalId<Trainer>,
    gymId: string,
  ) => Promise<boolean>;
  updateTrainer: (trainer: Trainer) => Promise<boolean>;
  getJetsonsHealth: (gymId: string) => Promise<JetsonHealthDTO[]>;
}

export const UserActionContext = createContext<UserActionContextProps>({
  updateExercise: () => Promise.resolve(false),
  createExercise: () => Promise.resolve(false),
  deleteUserExercise: () => Promise.resolve(false),
  uploadUserExerciseImages: () => Promise.resolve([]),
  uploadUserExerciseVideos: () => Promise.resolve([]),
  createGroup: () => Promise.resolve(false),
  updateGroup: () => Promise.resolve(false),
  deleteGroup: () => Promise.resolve(false),
  updateGym: () => Promise.resolve(false),
  createManager: () => Promise.resolve(false),
  updateManager: () => Promise.resolve(false),
  createMember: () => Promise.resolve(false),
  updateMember: () => Promise.resolve(false),
  updateMemberSubscriptions: () => Promise.resolve(false),
  createProgram: () =>
    Promise.resolve({
      success: false,
      program: null,
    }),
  updateProgramAndRefreshPrograms: () => Promise.resolve(false),
  deleteProgram: () => Promise.resolve(false),
  assignProgramAndRefreshMembers: () => Promise.resolve(false),
  getSessionStatistics: () => Promise.resolve({}),
  getLastSessionStatistics: () => Promise.resolve({}),
  getLastSessions: () => Promise.resolve(null),
  createRoutineTemplate: () => Promise.resolve(false),
  updateTemplate: () => Promise.resolve(false),
  deleteTemplate: () => Promise.resolve(false),
  createTrainer: () => Promise.resolve(false),
  updateTrainer: () => Promise.resolve(false),
  getJetsonsHealth: () => Promise.resolve([]),
} as UserActionContextProps);

export const UserActionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { setGlobalError } = useErrorContext();
  const { upsertSessionExercise, removeSessionExercise } = useGlobalAction();
  const {
    loginManager,
    loginTrainer,
    gym,
    members,
    programs: cachedPrograms = {},
    templates: cachedTemplates = {},
  } = useSessionData();
  const sessionStatisticsCacheRef = useRef<
    CacheStore<Record<string, SessionStatistic[]>>
  >({});
  const lastSessionStatisticsCacheRef = useRef<
    CacheStore<Record<string, SessionStatistic>>
  >({});
  const lastSessionsCacheRef = useRef<
    CacheStore<Record<string, TrainingRoutineSession>>
  >({});

  useEffect(() => {
    sessionStatisticsCacheRef.current = {};
    lastSessionStatisticsCacheRef.current = {};
    lastSessionsCacheRef.current = {};
    logCache('cache:clear', { gymId: gym?.id });
  }, [gym?.id]);
  const {
    refreshExercise,
    refreshExercisesAfterDelete,
    refreshGroupsAfterDelete,
    refreshProgramsAfterDelete,
    refreshTemplatesAfterDelete,
    refreshGroup,
    refreshMembers,
    removeMemberAssociations,
    refreshGym,
    refreshManager,
    removeManager,
    refreshTrainer,
    refreshMember,
    refreshProgram,
    refreshTemplate,
    refreshGymProducts,
    removeTrainer,
  } = useSessionAction();

  const syncMemberGroupAssignments = async (
    groupId: string,
    memberIdsToAdd: string[],
    memberIdsToRemove: string[],
  ): Promise<boolean> => {
    const adds = Array.from(new Set(memberIdsToAdd ?? []));
    const removes = Array.from(new Set(memberIdsToRemove ?? []));
    if (!adds.length && !removes.length) {
      return true;
    }
    const stagedMembers: Record<string, Member> = {};
    const missingMemberIds = new Set<string>();

    const stageUpdate = (
      memberId: string,
      mutator: (groupIds: Set<string>) => void,
    ) => {
      const baseMember = stagedMembers[memberId] ?? members[memberId];
      if (!baseMember) {
        missingMemberIds.add(memberId);
        return;
      }
      const nextGroupIds = new Set(baseMember.groupIds ?? []);
      mutator(nextGroupIds);
      stagedMembers[memberId] = {
        ...baseMember,
        groupIds: Array.from(nextGroupIds),
      };
    };

    adds.forEach((memberId) =>
      stageUpdate(memberId, (groupIds) => groupIds.add(groupId)),
    );
    removes.forEach((memberId) =>
      stageUpdate(memberId, (groupIds) => groupIds.delete(groupId)),
    );

    if (missingMemberIds.size) {
      const refreshSuccess = await refreshMembers(
        Array.from(missingMemberIds.values()),
      );
      if (!refreshSuccess) {
        return false;
      }
    }

    Object.values(stagedMembers).forEach((member) => refreshMember(member));
    return true;
  };

  const applyExerciseLibraryUpdates = (
    exercise: Exercise,
    options?: { isPersonalized?: boolean },
  ): void => {
    if (!exercise?.id) {
      return;
    }
    const isPersonalizedExercise =
      typeof options?.isPersonalized === 'boolean'
        ? options.isPersonalized
        : Boolean(exercise.isPersonalized);
    if (!isPersonalizedExercise) {
      upsertSessionExercise(exercise);
    }
  };

  const removeExerciseFromLibraries = (exerciseId: string): void => {
    if (!exerciseId) {
      return;
    }
    removeSessionExercise(exerciseId);
  };

  const loadFreshMember = async (memberId: string): Promise<Member | null> => {
    if (!memberId) {
      setGlobalError('No member id provided');
      return null;
    }
    const {
      data: fetchedMembers,
      success,
      error,
    } = await fetchMembersByIds([memberId]);
    if (!success || !fetchedMembers?.length) {
      setGlobalError(error?.message || 'Error refreshing member');
      return null;
    }
    const freshMember = fetchedMembers[0];
    refreshMember(freshMember);
    return freshMember;
  };

  const loadFreshGroup = async (groupId: string): Promise<Group | null> => {
    if (!groupId) {
      setGlobalError('No group id provided');
      return null;
    }
    const {
      data: fetchedGroups,
      success,
      error,
    } = await fetchGroupsByIds([groupId]);
    if (!success || !fetchedGroups?.length) {
      setGlobalError(error?.message || 'Error refreshing group');
      return null;
    }
    const freshGroup = fetchedGroups[0];
    refreshGroup(freshGroup);
    return freshGroup;
  };

  const loadFreshTrainer = async (
    trainerId: string,
  ): Promise<Trainer | null> => {
    if (!trainerId) {
      setGlobalError('No trainer id provided');
      return null;
    }
    const {
      data: fetchedTrainers,
      success,
      error,
    } = await fetchTrainersByIds([trainerId]);
    if (!success || !fetchedTrainers?.length) {
      setGlobalError(error?.message || 'Error refreshing trainer');
      return null;
    }
    const freshTrainer = fetchedTrainers[0];
    refreshTrainer(freshTrainer);
    return freshTrainer;
  };

  const loadFreshManager = async (
    managerId: string,
  ): Promise<Manager | null> => {
    if (!managerId) {
      setGlobalError('No manager id provided');
      return null;
    }
    const {
      data: fetchedManagers,
      success,
      error,
    } = await fetchManagersByIds([managerId]);
    if (!success || !fetchedManagers?.length) {
      setGlobalError(error?.message || 'Error refreshing manager');
      return null;
    }
    const freshManager = fetchedManagers[0];
    refreshManager(freshManager);
    return freshManager;
  };

  const getProgramFromCache = (
    programId: string | undefined,
    trainerId?: string,
  ): Program | null => {
    if (!programId) {
      return null;
    }
    const cachedProgram = cachedPrograms[programId];
    if (!cachedProgram) {
      return null;
    }
    if (
      trainerId &&
      cachedProgram.trainerId &&
      cachedProgram.trainerId !== trainerId
    ) {
      return null;
    }
    refreshProgram(cachedProgram);
    return cachedProgram;
  };

  const getTemplateFromCache = (
    templateId: string | undefined,
    trainerIds?: string[],
  ): TrainingRoutineTemplate | null => {
    if (!templateId) {
      return null;
    }
    const cachedTemplate = cachedTemplates[templateId];
    if (!cachedTemplate) {
      return null;
    }
    if (
      trainerIds?.length &&
      !(cachedTemplate.trainerIds ?? []).some((trainerId) =>
        trainerIds.includes(trainerId),
      )
    ) {
      return null;
    }
    refreshTemplate(cachedTemplate);
    return cachedTemplate;
  };

  const loadFreshProgram = async (
    programId: string,
    trainerId?: string,
  ): Promise<Program | null> => {
    if (!programId) {
      setGlobalError('Unable to refresh program without trainer context');
      return null;
    }
    const cachedProgram = getProgramFromCache(programId, trainerId);
    if (cachedProgram) {
      return cachedProgram;
    }
    const trainerIdsToQuery = (
      trainerId ? [trainerId] : loginTrainer?.id ? [loginTrainer.id] : []
    ).filter(Boolean);
    if (!trainerIdsToQuery.length) {
      setGlobalError('Unable to refresh program without trainer context');
      return null;
    }
    for (const trainerIdentifier of trainerIdsToQuery) {
      const {
        data: trainerPrograms,
        success,
        error,
      } = await fetchTrainerPrograms(trainerIdentifier);
      if (!success || !trainerPrograms) {
        setGlobalError(error?.message || 'Error refreshing program');
        return null;
      }
      const match = trainerPrograms.find((item) => item.id === programId);
      if (match) {
        refreshProgram(match);
        return match;
      }
    }
    setGlobalError('Program not found');
    return null;
  };

  const loadFreshTemplate = async (
    templateId: string,
    trainerIds?: string[],
  ): Promise<TrainingRoutineTemplate | null> => {
    if (!templateId) {
      setGlobalError('Unable to refresh template without trainer context');
      return null;
    }
    const cachedTemplate = getTemplateFromCache(templateId, trainerIds);
    if (cachedTemplate) {
      return cachedTemplate;
    }
    const trainerIdsToQuery =
      trainerIds && trainerIds.length
        ? trainerIds
        : loginTrainer?.id
          ? [loginTrainer.id]
          : [];
    if (!trainerIdsToQuery.length) {
      setGlobalError('Unable to refresh template without trainer context');
      return null;
    }
    for (const trainerIdentifier of trainerIdsToQuery) {
      const {
        data: trainerTemplates,
        success,
        error,
      } = await fetchTrainerTemplates(trainerIdentifier);
      if (!success || !trainerTemplates) {
        setGlobalError(error?.message || 'Error refreshing template');
        return null;
      }
      const match = trainerTemplates.find((item) => item.id === templateId);
      if (match) {
        refreshTemplate(match);
        return match;
      }
    }
    setGlobalError('Template not found');
    return null;
  };

  const loadFreshGym = async (gymId?: string): Promise<Gym | null> => {
    const targetGymId = gymId ?? gym?.id;
    if (!targetGymId) {
      setGlobalError('No gym id provided');
      return null;
    }
    const { data: fetchedGym, success, error } = await fetchGym(targetGymId);
    if (!success || !fetchedGym) {
      setGlobalError(error?.message || 'Error refreshing gym');
      return null;
    }
    refreshGym(fetchedGym);
    return fetchedGym;
  };

  const getMembersAssignedToProgram = (programId?: string): Member[] => {
    if (!programId) {
      return [];
    }
    return Object.values(members).filter(
      (member) => member.program?.id === programId,
    );
  };

  const applyProgramUpdateToMembers = (program: Program): void => {
    const affectedMembers = getMembersAssignedToProgram(program.id);
    if (!affectedMembers.length) {
      return;
    }
    affectedMembers.forEach((member) => {
      if (!member.program) {
        return;
      }
      const updatedAssignedProgram: AssignedProgram = {
        ...member.program,
        ...program,
      };
      refreshMember({
        ...member,
        program: updatedAssignedProgram,
      });
    });
  };

  const clearProgramFromMembers = (programId: string): void => {
    const affectedMembers = getMembersAssignedToProgram(programId);
    if (!affectedMembers.length) {
      return;
    }
    affectedMembers.forEach((member) =>
      refreshMember({
        ...member,
        program: undefined,
      }),
    );
  };

  const updateExercise = async (
    oldExerciseData: Exercise,
    newExerciseData: Exercise,
  ): Promise<boolean> => {
    const {
      data: updatedExercise,
      success,
      error,
    } = await updateExerciseData(oldExerciseData, newExerciseData);

    if (!success || !updatedExercise) {
      setGlobalError(error?.message || 'Unknown error');
      return false;
    }

    refreshExercise(updatedExercise);
    applyExerciseLibraryUpdates(updatedExercise, {
      isPersonalized:
        typeof oldExerciseData.isPersonalized === 'boolean'
          ? oldExerciseData.isPersonalized
          : Boolean(updatedExercise.isPersonalized),
    });
    return true;
  };

  const createExercise = async (
    newExercise: WithOptionalId<Exercise>,
    options: {
      baseExercise: Exercise | null;
      isCreatingPersonalizedExercise?: boolean;
    },
  ): Promise<boolean> => {
    if (newExercise.id) {
      setGlobalError('Cannot create an existing exercise (id already exist).');
      return false;
    }
    if (
      options.baseExercise?.id &&
      options.isCreatingPersonalizedExercise &&
      !isMotivisionTrainer(loginTrainer)
    ) {
      newExercise.originalExerciseId = options.baseExercise.id;
    }
    const {
      data: exercise,
      success,
      error,
    } = isMotivisionTrainer(loginTrainer)
      ? await createGlobalExercise(newExercise)
      : await createPersonalizedExercise(newExercise);
    if (!success || !exercise) {
      setGlobalError(error?.message || 'Unknown error');
      return false;
    }
    const isPersonalizedExercise =
      options.isCreatingPersonalizedExercise ??
      !isMotivisionTrainer(loginTrainer);
    applyExerciseLibraryUpdates(exercise, {
      isPersonalized: isPersonalizedExercise,
    });
    refreshExercise(exercise);
    return true;
  };

  const deleteUserExercise = async (exerciseId: string): Promise<boolean> => {
    const { success, error } = await deleteExercise(exerciseId);
    if (!success) {
      setGlobalError(error?.message || 'Unknown error');
      return false;
    }
    removeExerciseFromLibraries(exerciseId);
    refreshExercisesAfterDelete(exerciseId, loginTrainer?.id ?? '');
    return true;
  };

  const createGroup = async (
    newGroup: WithOptionalId<Group>,
    trainerIds: string[],
    memberIds: string[],
  ): Promise<boolean> => {
    const {
      data: group,
      success,
      error,
    } = await createNewGroup(newGroup, trainerIds, memberIds);

    if (!success || !group) {
      setGlobalError(error?.message || 'Unknown error');
      return false;
    }
    // Keep reducer state aligned with remote data and hydrate member caches.
    const membershipSynced = await syncMemberGroupAssignments(
      group.id,
      group.memberIds,
      [],
    );
    refreshGroup(group);
    return membershipSynced;
  };

  const updateGroup = async (
    oldGroupData: Group,
    newGroupData: Group,
  ): Promise<boolean> => {
    const baselineGroup = await loadFreshGroup(oldGroupData.id);
    if (!baselineGroup) {
      return false;
    }
    const oldTrainerIds = _.difference(
      baselineGroup.trainerIds ?? [],
      newGroupData.trainerIds ?? [],
    );
    const oldMemberIds = _.difference(
      baselineGroup.memberIds ?? [],
      newGroupData.memberIds ?? [],
    );
    const newMemberIds = _.difference(
      newGroupData.memberIds ?? [],
      baselineGroup.memberIds ?? [],
    );
    const trainerIdsToRemove = oldTrainerIds;

    const {
      data: updatedGroup,
      success,
      error,
    } = await updateGroupData(baselineGroup, newGroupData);

    if (!success || !updatedGroup) {
      setGlobalError(error?.message || 'Unknown error');
      return false;
    }

    // Refresh reducer slices for trainers/members that reference this group.
    const membershipSynced = await syncMemberGroupAssignments(
      updatedGroup.id,
      newMemberIds,
      oldMemberIds,
    );
    trainerIdsToRemove.forEach((trainerId) =>
      refreshGroupsAfterDelete(updatedGroup.id, trainerId),
    );
    refreshGroup(updatedGroup);
    return membershipSynced;
  };

  const deleteGroup = async (group: Group): Promise<boolean> => {
    const baselineGroup = await loadFreshGroup(group.id);
    if (!baselineGroup) {
      return false;
    }
    const { success, error } = await deleteGroupById(baselineGroup.id);
    if (!success) {
      setGlobalError(error?.message || 'Unknown error');
      return false;
    }
    // Remove the group from trainer dashboards and local member caches.
    const membershipSynced = await syncMemberGroupAssignments(
      baselineGroup.id,
      [],
      baselineGroup.memberIds ?? [],
    );
    (baselineGroup.trainerIds ?? []).forEach((trainerId) => {
      refreshGroupsAfterDelete(baselineGroup.id, trainerId);
    });
    return membershipSynced;
  };

  const updateGym = async (gym: Gym): Promise<boolean> => {
    const baselineGym = await loadFreshGym(gym.id);
    if (!baselineGym) {
      return false;
    }
    const {
      data: updatedGym,
      success,
      error,
    } = await updateGymData({
      ...baselineGym,
      ...gym,
    });
    if (!success || !updatedGym) {
      setGlobalError(error?.message || 'Error updating gym');
      return false;
    }
    // Push the updated gym into Redux so selectors reflect the latest metadata.
    refreshGym(updatedGym);
    const productsRefreshed = await refreshGymProducts(updatedGym.id);
    return productsRefreshed;
  };

  const createManager = async (
    manager: WithOptionalId<Manager>,
  ): Promise<boolean> => {
    if (!gym?.id) {
      setGlobalError('Gym not found');
      return false;
    }
    const {
      data: createdManager,
      success,
      error,
    } = await createNewManager(manager, gym.id);

    if (!success || !createdManager) {
      setGlobalError(error?.message || 'Error creating manager');
      return false;
    }

    refreshManager(createdManager);
    return true;
  };

  const updateManager = async (manager: Manager): Promise<boolean> => {
    if (loginManager?.id === manager.id && manager.isActive === false) {
      setGlobalError('You cannot deactivate the currently logged-in manager');
      return false;
    }
    const baselineManager = await loadFreshManager(manager.id);
    if (!baselineManager) {
      return false;
    }
    const {
      data: updatedManager,
      success,
      error,
    } = await updateManagerData({
      ...baselineManager,
      ...manager,
    });

    if (!success || !updatedManager) {
      setGlobalError(error?.message || 'Error updating manager');
      return false;
    }
    if (!updatedManager.isActive) {
      removeManager(updatedManager.id);
    } else {
      refreshManager(updatedManager);
    }
    return true;
  };

  const createMember = async (
    member: WithOptionalId<Member>,
    trainerId?: string,
  ): Promise<boolean | string> => {
    if (!gym) {
      setGlobalError('Gym not found');
      return false;
    }
    const {
      data: createdMember,
      success,
      error,
    } = await createNewMember(member, gym, trainerId);
    if (!success || !createdMember) {
      const message = error?.message || 'Error creating member';
      if (!message.toLowerCase().includes('pin already in use')) {
        setGlobalError(message);
      }
      return message;
    }
    // New members must be available to dashboards and trainer caches.
    refreshMember(createdMember);
    return true;
  };

  const updateMember = async (
    oldMemberData: Member,
    newMemberData: Member,
  ): Promise<boolean | string> => {
    const baselineMember = await loadFreshMember(oldMemberData.id);
    if (!baselineMember) {
      return false;
    }
    const trainerIdsToUnAssociate = _.difference(
      baselineMember.trainerIds,
      newMemberData.trainerIds,
    );
    const {
      data: updatedMember,
      success,
      error,
    } = await updateMemberData(baselineMember, newMemberData);
    if (!success || !updatedMember) {
      const message = error?.message || 'Error updating member';
      if (message.toLowerCase().includes('pin already in use')) {
        return message;
      }
      setGlobalError(message);
      return false;
    }
    // Update trainer dashboards and member caches after the mutation.
    removeMemberAssociations(updatedMember.id, trainerIdsToUnAssociate);
    refreshMember(updatedMember);
    return success;
  };

  const createProgram = async (
    program: WithOptionalId<Program>,
    trainerId: string,
  ): Promise<{ success: boolean; program: Program | null }> => {
    const {
      data: createdProgram,
      success,
      error,
    } = await createNewProgram(program, trainerId);
    if (!success || !createdProgram) {
      setGlobalError(error?.message || 'Error creating program');
      return { success: false, program: null };
    }
    refreshProgram(createdProgram);
    return { success: true, program: createdProgram };
  };

  const updateProgramAndRefreshPrograms = async (
    program: Program,
  ): Promise<boolean> => {
    const baselineProgram = await loadFreshProgram(
      program.id,
      program.trainerId,
    );
    if (!baselineProgram) {
      return false;
    }
    const {
      data: updatedProgram,
      success,
      error,
    } = await updateProgramData({
      ...baselineProgram,
      ...program,
    });
    if (!success || !updatedProgram) {
      setGlobalError(error?.message || 'Error updating program');
      return false;
    }
    // Record the program update locally and reapply it to affected members.
    refreshProgram(updatedProgram);
    applyProgramUpdateToMembers(updatedProgram);
    return true;
  };

  const deleteProgram = async (program: Program): Promise<boolean> => {
    const baselineProgram = await loadFreshProgram(
      program.id,
      program.trainerId,
    );
    if (!baselineProgram) {
      return false;
    }
    const { success, error } = await deleteProgramById(baselineProgram.id);
    if (!success) {
      setGlobalError(error?.message || 'Error deleting program');
      return false;
    }
    // Remove the deleted program from trainer dashboards and member caches.
    refreshProgramsAfterDelete(baselineProgram.id, baselineProgram.trainerId);
    clearProgramFromMembers(baselineProgram.id);
    return true;
  };

  const assignProgramAndRefreshMembers = async (
    memberIds: string[],
    program: AssignedProgram,
  ): Promise<boolean> => {
    const {
      data: updatedMembers,
      success,
      error,
    } = await assignProgramToMembers(memberIds, program);
    if (!success) {
      setGlobalError(error?.message || 'Error assigning program');
      return false;
    }
    if (!updatedMembers || !updatedMembers.length) {
      // Some environments respond without hydrated members even though the assignment
      // completed. Refresh the affected members so Redux stays in sync.
      return refreshMembers(memberIds);
    }
    try {
      updatedMembers.forEach((member) => refreshMember(_.cloneDeep(member)));
      return true;
    } catch (refreshError) {
      console.error(
        'Failed to refresh assigned members locally, refetching from server.',
        refreshError,
      );
      return refreshMembers(memberIds);
    }
  };

  const getSessionStatistics = async (
    memberIds: string[],
    from: string,
    to: string,
    useDefaultFromDate?: boolean,
  ): Promise<Record<string, SessionStatistic[]>> => {
    if (!memberIds?.length) {
      return {};
    }
    const cacheKey = buildCacheKey(memberIds, {
      from: from ?? null,
      to: to ?? null,
      useDefaultFromDate: Boolean(useDefaultFromDate),
    });
    const cachedValue = getCachedValue(
      sessionStatisticsCacheRef,
      cacheKey,
      CACHE_NAMES.SESSION_STATS,
    );
    if (cachedValue) {
      return cachedValue;
    }
    const { data, success, error } = await fetchSessionStatistics(
      memberIds,
      from,
      to,
      useDefaultFromDate,
    );
    if (!success || !data) {
      setGlobalError(error?.message || 'Error fetching session statistics');
      return {};
    }
    setCachedValue(
      sessionStatisticsCacheRef,
      cacheKey,
      data,
      CACHE_NAMES.SESSION_STATS,
    );
    return data;
  };

  const getLastSessionStatistics = async (
    memberIds: string[],
  ): Promise<Record<string, SessionStatistic>> => {
    if (!memberIds?.length) {
      return {};
    }
    const cacheKey = buildCacheKey(memberIds);
    const cachedValue = getCachedValue(
      lastSessionStatisticsCacheRef,
      cacheKey,
      CACHE_NAMES.LAST_STATS,
    );
    if (cachedValue) {
      return cachedValue;
    }
    const { data, success, error } =
      await fetchLastSessionStatistics(memberIds);
    if (!success || !data) {
      setGlobalError(
        error?.message || 'Error fetching last session statistics',
      );
      return {};
    }
    setCachedValue(
      lastSessionStatisticsCacheRef,
      cacheKey,
      data,
      CACHE_NAMES.LAST_STATS,
    );
    return data;
  };

  const getLastSessions = async (
    memberIds: string[],
  ): Promise<Record<string, TrainingRoutineSession> | null> => {
    if (!memberIds?.length) {
      return null;
    }
    const cacheKey = buildCacheKey(memberIds);
    const cachedValue = getCachedValue(
      lastSessionsCacheRef,
      cacheKey,
      CACHE_NAMES.LAST_SESSIONS,
    );
    if (cachedValue) {
      return cachedValue;
    }
    const { data, success, error } = await fetchLastSessions(memberIds);
    if (!success || !data) {
      setGlobalError(error?.message || 'Error fetching last sessions');
      return null;
    }
    setCachedValue(
      lastSessionsCacheRef,
      cacheKey,
      data,
      CACHE_NAMES.LAST_SESSIONS,
    );
    return data;
  };

  const createRoutineTemplate = async (
    template: WithOptionalId<TrainingRoutineTemplate>,
    trainerId: string,
  ): Promise<boolean> => {
    const {
      data: createdTemplate,
      success,
      error,
    } = await createNewTemplate(template, trainerId);
    if (!success || !createdTemplate) {
      setGlobalError(error?.message || 'Error creating routine template');
      return false;
    }
    // Surface the created template in the consuming dashboards.
    refreshTemplate(createdTemplate);
    return true;
  };

  const updateTemplate = async (
    template: TrainingRoutineTemplate,
  ): Promise<boolean> => {
    const baselineTemplate = await loadFreshTemplate(
      template.id,
      template.trainerIds,
    );
    if (!baselineTemplate) {
      return false;
    }
    const {
      data: updatedTemplate,
      success,
      error,
    } = await updateTemplateData({
      ...baselineTemplate,
      ...template,
    });

    if (!success || !updatedTemplate) {
      setGlobalError(error?.message || 'Error updating routine template');
      return false;
    }
    // Persist the refreshed template locally for any open editors.
    refreshTemplate(updatedTemplate);
    return true;
  };

  const deleteTemplate = async (
    template: TrainingRoutineTemplate,
  ): Promise<boolean> => {
    const baselineTemplate = await loadFreshTemplate(
      template.id,
      template.trainerIds,
    );
    if (!baselineTemplate) {
      return false;
    }
    const { success, error } = await deleteTemplateById(baselineTemplate.id);

    if (!success) {
      setGlobalError(error?.message || 'Error deleting routine template');
      return false;
    }
    // Strip the template from every trainer dashboard and template cache.
    (baselineTemplate.trainerIds ?? []).forEach((trainerId) =>
      refreshTemplatesAfterDelete(baselineTemplate.id, trainerId),
    );
    return true;
  };

  const createTrainer = async (
    trainer: WithOptionalId<Trainer>,
    gymId: string,
  ): Promise<boolean> => {
    const {
      data: createdTrainer,
      success,
      error,
    } = await createNewTrainer(trainer, gymId);

    if (!success || !createdTrainer) {
      setGlobalError(error?.message || 'Error creating trainer');
      return false;
    }
    // Insert the created trainer into Redux immediately.
    refreshTrainer(createdTrainer);
    return true;
  };

  const updateTrainer = async (trainer: Trainer): Promise<boolean> => {
    const baselineTrainer = await loadFreshTrainer(trainer.id);
    if (!baselineTrainer) {
      return false;
    }
    const {
      data: updatedTrainer,
      success,
      error,
    } = await updateTrainerData({
      ...baselineTrainer,
      ...trainer,
    });

    if (!success || !updatedTrainer) {
      setGlobalError(error?.message || 'Error updating trainer');
      return false;
    }
    // Ensure the refreshed trainer is reflected in Redux selectors.
    if (updatedTrainer.isActive === false) {
      removeTrainer(updatedTrainer.id);
    } else {
      refreshTrainer(updatedTrainer);
    }
    return true;
  };

  const uploadUserExerciseImages = async (
    files: File[],
    trainerId: string,
  ): Promise<FileResponse[]> => {
    const { data, success, error } = await uploadExerciseImages(
      files,
      trainerId,
    );
    if (!success) {
      setGlobalError(error?.message || 'Error uploading exercise images');
      return [];
    }
    return data;
  };

  const uploadUserExerciseVideos = async (
    files: File[],
    trainerId: string,
  ): Promise<FileResponse[]> => {
    const { data, success, error } = await uploadExerciseVideos(
      files,
      trainerId,
    );
    if (!success) {
      setGlobalError(error?.message || 'Error uploading exercise videos');
      return [];
    }
    return data;
  };

  const updateMemberSubscriptions = async (
    oldMemberData: Member,
    newMemberData: Member,
    newSelectedProduct: Product | '',
    gymProducts: GymProduct[],
  ): Promise<boolean> => {
    const baselineMember = await loadFreshMember(oldMemberData.id);
    if (!baselineMember) {
      return false;
    }
    const {
      data: updatedMember,
      success,
      error,
    } = await updateMemberAndSubscriptions(
      baselineMember,
      newMemberData,
      newSelectedProduct,
      gymProducts,
    );
    if (!success) {
      setGlobalError(
        error?.message || 'Error updating member and subscriptions',
      );
      return false;
    }
    if (updatedMember) {
      refreshMember(updatedMember);
    }
    return true;
  };

  const getJetsonsHealth = async (
    gymId: string,
  ): Promise<JetsonHealthDTO[]> => {
    const { data, success, error } = await getJetsonsHealthByGymId(gymId);
    if (!success) {
      setGlobalError(error?.message || 'Error fetching jetsons health');
      return [];
    }
    return data ?? [];
  };

  return (
    <UserActionContext.Provider
      value={{
        updateExercise,
        createExercise,
        deleteUserExercise,
        uploadUserExerciseImages,
        uploadUserExerciseVideos,
        createGroup,
        updateGroup,
        deleteGroup,
        updateGym,
        createManager,
        updateManager,
        createMember,
        updateMember,
        updateMemberSubscriptions,
        createProgram,
        updateProgramAndRefreshPrograms,
        deleteProgram,
        assignProgramAndRefreshMembers,
        getSessionStatistics,
        getLastSessionStatistics,
        getLastSessions,
        createRoutineTemplate,
        updateTemplate,
        deleteTemplate,
        createTrainer,
        updateTrainer,
        getJetsonsHealth,
      }}
    >
      {children}
    </UserActionContext.Provider>
  );
};

export const useUserAction = () => {
  return useContext(UserActionContext);
};
